import { createEvent, createStore } from 'effector'

// Client-side only: This module manages unread and new chat message state in-memory for the UI. No server-side persistence.

/**
 * Event: Mark messages as read for a chat.
 * @param chatId - The chat ID.
 * @param messageIds - Array of message IDs to mark as read.
 */
export const markMessagesAsRead = createEvent<{
  chatId: number
  messageIds: number[]
}>()

/**
 * Event: Mark messages as unread for a chat.
 * @param chatId - The chat ID.
 * @param messageIds - Array of message IDs to mark as unread.
 */
export const markMessagesAsUnread = createEvent<{
  chatId: number
  messageIds: number[]
}>()

/**
 * Event: Add a newly received message for a chat.
 * @param chatId - The chat ID.
 * @param messageId - The message ID to add as received.
 */
export const addReceivedMessage = createEvent<{
  chatId: number
  messageId: number
}>()

/**
 * Event: Remove a newly received message for a chat.
 * @param chatId - The chat ID.
 * @param messageId - The message ID to remove from received.
 */
export const removeReceivedMessage = createEvent<{
  chatId: number
  messageId: number
}>()

/**
 * Store: Maps chatId to a set of unread messageIds.
 */
export const $unreadMessages = createStore<Record<number, Set<number>>>({})

/**
 * Store: Maps chatId to a set of newly received messageIds.
 */
export const $newlyReceivedMessages = createStore<Record<number, Set<number>>>(
  {},
)

// Mark as read: remove messageIds from the set for the chat
$unreadMessages.on(markMessagesAsRead, (state, { chatId, messageIds }) => {
  const current = new Set(state[chatId] || [])

  messageIds.forEach((id) => current.delete(id))

  return { ...state, [chatId]: current }
})
// Mark as unread: add messageIds to the set for the chat
$unreadMessages.on(markMessagesAsUnread, (state, { chatId, messageIds }) => {
  const current = new Set(state[chatId] || [])

  messageIds.forEach((id) => current.add(id))

  return { ...state, [chatId]: current }
})

$newlyReceivedMessages.on(
  addReceivedMessage,
  (state, { chatId, messageId }) => {
    const current = new Set(state[chatId] || [])

    current.add(messageId)

    return { ...state, [chatId]: current }
  },
)

$newlyReceivedMessages.on(
  removeReceivedMessage,
  (state, { chatId, messageId }) => {
    const current = new Set(state[chatId] || [])

    current.delete(messageId)

    return { ...state, [chatId]: current }
  },
)

/**
 * Selector: Returns a store indicating if a message is unread in a chat.
 * @param chatId - The chat ID.
 * @param messageId - The message ID.
 * @returns Store<boolean>
 */
export const selectorIsMessageUnread = (chatId: number, messageId: number) =>
  $unreadMessages.map((state) => !!state[chatId]?.has(messageId))

/**
 * Selector: Returns a store indicating if a message is newly received in a chat.
 * @param chatId - The chat ID.
 * @param messageId - The message ID.
 * @returns Store<boolean>
 */
export const selectorIsNewMessage = (chatId: number, messageId: number) =>
  $newlyReceivedMessages.map((state) => !!state[chatId]?.has(messageId))

/**
 * Selector: Returns a store indicating if a chat has any unread messages.
 * @param chatId - The chat ID.
 * @returns Store<boolean>
 */
export const selectorHasUnreadMessagesByChatId = (chatId: number) =>
  $unreadMessages.map(
    (state) => !!state[chatId]?.size && state[chatId]?.size > 0,
  )
